import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import DataTable from '../components/ResponsiveDataTable';
import useIsMobile from '../hooks/useIsMobile';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import {
  getCrmLeads,
  downloadCrmLeads,
  getCrmLeadTagOptions,
  getCrmAvailableViews,
  getCrmSavedFilters,
  createCrmSavedFilter,
  deleteCrmSavedFilter,
  getHubs,
  getCrmVariables,
  searchCrmAssignableStaff,
} from '../config/api';
import CrmCallDialog from '../components/CrmCallDialog';
import CrmBulkAssignHubDialog from '../components/CrmBulkAssignHubDialog';
import CrmBulkConfirmHubSuggestionsDialog from '../components/CrmBulkConfirmHubSuggestionsDialog';
import CrmBulkAssignUserDialog from '../components/CrmBulkAssignUserDialog';
import CrmBulkTagDialog from '../components/CrmBulkTagDialog';
import CrmBulkSetVariableDialog from '../components/CrmBulkSetVariableDialog';
import CrmVariableCsvImportDialog from '../components/CrmVariableCsvImportDialog';
import CrmLeadCsvImportDialog from '../components/CrmLeadCsvImportDialog';
import CrmArchiveLeadDialog from '../components/CrmArchiveLeadDialog';
import CrmDeleteLeadDialog from '../components/CrmDeleteLeadDialog';
import CrmStageDialog from '../components/CrmStageDialog';
import CrmDeviceDialDialog from '../components/CrmDeviceDialDialog';
import { deviceDialNumber } from '../utils/crmPhone';

const STAGE_OPTIONS = [
  { value: 'not_contacted', label: 'Not contacted' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'interested', label: 'Interested' },
  { value: 'app_registered', label: 'App registered' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const CREATED_FROM_OPTIONS = [
  { value: 'pmsg', label: 'PMSG' },
  { value: 'manual', label: 'Manual' },
  { value: 'oms_signup', label: 'Signup' },
];

const STAGE_LABELS = STAGE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const DEFAULT_FILTERS = {
  search: '',
  stage: [],
  created_from: '',
  tags: [],
  unread_whatsapp: false,
  cart_active_no_order: false,
  order_presence: '',
  follow_up_status: '',
  video_call_status: '',
  // Spec §14 filters (backend-supported)
  assigned_user_id: '',
  last_contact_after: '',
  last_contact_before: '',
  pmsg_state: '',
  pmsg_district: '',
  pmsg_address: '',
  installs_min: '',
  installs_max: '',
  district_installs_min: '',
  district_installs_max: '',
  first_seen_after: '',
  first_seen_before: '',
  last_seen_after: '',
  last_seen_before: '',
  hub_id: '',
  oms_matched: '',
  registered_after: '',
  registered_before: '',
  last_login_after: '',
  last_login_before: '',
  last_order_after: '',
  last_order_before: '',
  days_since_last_order: '',
  orders_30_count_min: '',
  orders_30_count_max: '',
  orders_30_value_min: '',
  orders_30_value_max: '',
  orders_90_count_min: '',
  orders_90_count_max: '',
  orders_90_value_min: '',
  orders_90_value_max: '',
  lifetime_value_min: '',
  lifetime_value_max: '',
  average_order_value_min: '',
  average_order_value_max: '',
  top_brand: '',
  current_orders_min: '',
  current_orders_max: '',
};

const ORDER_FILTER_GROUPS = [
  ['orders_30_count_min', 'orders_30_count_max'],
  ['orders_30_value_min', 'orders_30_value_max'],
  ['orders_90_count_min', 'orders_90_count_max'],
  ['orders_90_value_min', 'orders_90_value_max'],
  ['lifetime_value_min', 'lifetime_value_max'],
  ['average_order_value_min', 'average_order_value_max'],
  ['current_orders_min', 'current_orders_max'],
  ['top_brand'],
];

const PMSG_INSTALL_FILTER_GROUPS = [
  ['installs_min', 'installs_max'],
  ['district_installs_min', 'district_installs_max'],
];

const ADVANCED_FILTER_KEYS = [
  'assigned_user_id', 'pmsg_state', 'pmsg_district', 'pmsg_address', 'hub_id',
  'oms_matched', 'days_since_last_order', 'last_contact_after',
  'last_contact_before', 'last_order_after', 'last_order_before',
  'first_seen_after', 'first_seen_before', 'last_seen_after', 'last_seen_before',
  'registered_after', 'registered_before', 'last_login_after', 'last_login_before',
];

// Sentinel option for the "Assigned user" filter to show leads with no owner.
// Its value is sent as assigned_user_id=unassigned; the backend maps it to
// assigned_user_id IS NULL.
const UNASSIGNED_OPTION = { value: 'unassigned', label: '— Unassigned —' };
const NO_HUB_OPTION = { value: 'no_hub', label: '— No hub —' };

const money = (v) =>
  `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const shortDateTime = (value) =>
  value
    ? new Date(value).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

const latestActivity = (accounts, field) =>
  accounts.reduce((latest, account) => {
    if (!account[field]) return latest;
    if (!latest || new Date(account[field]) > new Date(latest[field])) return account;
    return latest;
  }, null);

const accountActivity = (row) => {
  const accounts = row.appAndCart?.accounts || [];
  if (!accounts.length) return <span className="crm-muted">No linked account</span>;
  const registered = latestActivity(accounts, 'registeredAt');
  const login = latestActivity(accounts, 'lastLoginAt');
  const cart = latestActivity(accounts, 'lastCartActivityAt');
  return (
    <div className="crm-account-summary">
      <div className="crm-account-count">
        {accounts.length} linked account{accounts.length === 1 ? '' : 's'}
      </div>
      <div className="crm-account-activity-row">
        <span>Registered</span><strong>{shortDateTime(registered?.registeredAt)}</strong>
      </div>
      <div className="crm-account-activity-row">
        <span>Last login</span><strong>{shortDateTime(login?.lastLoginAt)}</strong>
      </div>
      <div className="crm-account-activity-row">
        <span>Cart activity</span><strong>{shortDateTime(cart?.lastCartActivityAt)}</strong>
      </div>
    </div>
  );
};

const compactChips = (items, labelFor, emptyLabel = '—') => {
  if (!items.length) return <span className="crm-muted">{emptyLabel}</span>;
  const visible = items.slice(0, 2);
  return (
    <div className="crm-compact-chips" title={items.map(labelFor).join(', ')}>
      {visible.map((item, index) => (
        <span key={item.id || `${labelFor(item)}-${index}`} className="crm-chip">
          {labelFor(item)}
        </span>
      ))}
      {items.length > visible.length && (
        <span className="crm-chip crm-chip-more">+{items.length - visible.length}</span>
      )}
    </div>
  );
};

const isOverdue = (d) => {
  if (!d) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(d) < today;
};

const RangeFilter = ({ label, minName, maxName, filters, onChange, moneyRange = false }) => (
  <div className="crm-range-filter">
    <label>{label}</label>
    <div className="crm-range-inputs">
      <input
        type="number"
        name={minName}
        className="form-control"
        value={filters[minName]}
        onChange={onChange}
        placeholder={moneyRange ? '₹ min' : 'Min'}
        aria-label={`${label} minimum`}
        min="0"
        step={moneyRange ? '1' : '1'}
      />
      <span>to</span>
      <input
        type="number"
        name={maxName}
        className="form-control"
        value={filters[maxName]}
        onChange={onChange}
        placeholder={moneyRange ? '₹ max' : 'Max'}
        aria-label={`${label} maximum`}
        min="0"
        step={moneyRange ? '1' : '1'}
      />
    </div>
  </div>
);

const CrmLeads = () => {
  const isMobile = useIsMobile();
  // Phones: the filter bars start folded so the lead list is visible first.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
  const isCrmStaff = userData.role === 'user';
  const loggedInAssigneeOption = isCrmStaff && userData.id ? {
    value: userData.id,
    label: userData.name || userData.email || 'Logged-in user',
  } : null;
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 25, totalPages: 0 });
  const [page, setPage] = useState(1);

  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    assigned_user_id: loggedInAssigneeOption?.value || '',
  }));
  // Server-side sort selected by clicking a sortable column header.
  const [sort, setSort] = useState(null); // { field, direction } | null

  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedSavedFilter, setSelectedSavedFilter] = useState(null);
  const [availableViews, setAvailableViews] = useState([]);
  const [availableViewsLoaded, setAvailableViewsLoaded] = useState(false);
  const [selectedCrmView, setSelectedCrmView] = useState(null);

  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelectedRowsToggle, setClearSelectedRowsToggle] = useState(false);
  const [bulkHubDialogOpen, setBulkHubDialogOpen] = useState(false);
  const [confirmHubSuggestionsOpen, setConfirmHubSuggestionsOpen] = useState(false);
  const [bulkUserDialogOpen, setBulkUserDialogOpen] = useState(false);
  const [bulkTagDialogOpen, setBulkTagDialogOpen] = useState(false);
  const [bulkVariableDialogOpen, setBulkVariableDialogOpen] = useState(false);
  const [variableCsvDialogOpen, setVariableCsvDialogOpen] = useState(false);
  const [leadCsvDialogOpen, setLeadCsvDialogOpen] = useState(false);
  const [variableDefinitions, setVariableDefinitions] = useState([]);
  const [callTarget, setCallTarget] = useState(null); // lead being called
  const [dialTarget, setDialTarget] = useState(null); // unregistered contact called via device dialer
  const [archiveView, setArchiveView] = useState('active');
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [stageTarget, setStageTarget] = useState(null);
  const [exportingLeads, setExportingLeads] = useState(false);

  // Filter dropdown option sources (friendly names instead of raw ids).
  const [hubOptions, setHubOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [assignedUserOption, setAssignedUserOption] = useState(loggedInAssigneeOption); // {value,label} for the selected assignee

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '32px',
      border: state.isFocused ? '1px solid #0d6efd' : '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '12px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(13, 110, 253, 0.25)' : 'none',
      '&:hover': { borderColor: state.isFocused ? '#0d6efd' : '#ced4da' },
    }),
    placeholder: (provided) => ({ ...provided, color: '#6c757d', fontSize: '12px' }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : '#212529',
      fontSize: '12px',
      padding: '8px 12px',
      cursor: 'pointer',
    }),
    multiValue: (provided) => ({ ...provided, backgroundColor: '#e7f3ff', fontSize: '11px' }),
    multiValueLabel: (provided) => ({ ...provided, color: '#0d6efd', fontSize: '11px' }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#0d6efd',
      ':hover': { backgroundColor: '#0d6efd', color: 'white' },
    }),
    menu: (provided) => ({ ...provided, zIndex: 9999, fontSize: '12px' }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    valueContainer: (provided) => ({ ...provided, padding: '2px 8px' }),
    input: (provided) => ({ ...provided, margin: '0px', fontSize: '12px' }),
    indicatorSeparator: (provided) => ({ ...provided, display: 'none' }),
    dropdownIndicator: (provided) => ({ ...provided, padding: '4px' }),
    clearIndicator: (provided) => ({ ...provided, padding: '4px' }),
  };

  const customStyles = {
    headRow: {
      style: {
        borderTop: '0',
        borderBottom: '1px solid #e3e9f1',
        backgroundColor: '#f4f7fb',
        minHeight: '44px',
      },
    },
    headCells: {
      style: {
        backgroundColor: '#f4f7fb',
        fontWeight: '700',
        fontSize: '11px',
        color: '#59677a',
        textTransform: 'uppercase',
        letterSpacing: '0.45px',
        padding: '10px 9px',
      },
    },
    cells: {
      style: {
        padding: '12px 10px',
        fontSize: '12px',
        alignItems: 'center',
        // Keep cell content inside its column so long chips/labels (e.g. a hub
        // with distance/serviceable) can't bleed into the neighbouring column.
        overflow: 'hidden',
      },
    },
    rows: {
      style: {
        '&:hover': { backgroundColor: '#f5f9ff' },
        borderBottomColor: '#e8edf3',
        minHeight: '66px',
      },
      stripedStyle: { backgroundColor: '#fbfcfe' },
    },
    pagination: {
      style: {
        borderTop: '1px solid #dee2e6',
        padding: '10px',
        minHeight: '40px',
        fontSize: '11px',
      },
    },
  };

  useEffect(() => {
    loadSavedFilters();
    loadAvailableViews();
    loadHubOptions();
    loadTagOptions();
    loadVariableDefinitions();
    // Initial option sources are loaded once when the Lead List opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAvailableViews = async () => {
    try {
      const res = await getCrmAvailableViews();
      const views = res?.data?.views || [];
      setAvailableViews(views);
      if (isCrmStaff && views.length) {
        const view = views[0];
        const option = {
          value: `crm-view-${view.id}`,
          sourceId: view.id,
          label: view.name,
          kind: 'crm-view',
          view,
        };
        setSelectedCrmView(option);
        setSelectedSavedFilter(null);
        await applyFilterSet(view.filters);
      }
    } catch (err) {
      console.error('Failed to load CRM views:', err);
      setAvailableViews([]);
    } finally {
      setAvailableViewsLoaded(true);
    }
  };

  const loadHubOptions = async () => {
    try {
      const res = await getHubs();
      const hubs = res?.data || res || [];
      const hubList = Array.isArray(hubs) ? hubs : [];
      setHubOptions(
        [
          NO_HUB_OPTION,
          ...hubList.flatMap((hub) => [
            {
              value: hub.id,
              label: hub.name || 'Unnamed hub',
            },
            ...(Array.isArray(hub.virtualHubs) ? hub.virtualHubs : []).map((child) => ({
              value: child.id,
              label: `${hub.name || 'Parent hub'} — ${child.name || 'Unnamed child hub'}`,
            })),
          ]),
        ]
      );
    } catch (err) {
      console.error('Failed to load hubs for filter:', err);
    }
  };

  const loadTagOptions = async () => {
    try {
      const response = await getCrmLeadTagOptions();
      const result = response?.data || response;
      setTagOptions((result?.tags || []).map((tag) => ({ value: tag, label: tag })));
    } catch (err) {
      console.error('Failed to load CRM tags:', err);
      setTagOptions([]);
    }
  };

  const loadVariableDefinitions = async () => {
    try {
      const response = await getCrmVariables();
      const result = response?.data || response;
      setVariableDefinitions(result?.variables || []);
    } catch (err) {
      console.error('Failed to load CRM variables:', err);
      setVariableDefinitions([]);
    }
  };

  // AsyncSelect loader for the assigned-user filter (staff search by name).
  const loadStaffOptions = async (search) => {
    try {
      const res = await searchCrmAssignableStaff(search);
      const result = res?.data || res;
      const staff = (result?.users || []).map((u) => ({
        value: u.id,
        label: u.name || u.email || 'Unnamed user',
      }));
      // Pin "Unassigned" at the top. Show it unless the search text clearly
      // doesn't match it, so users can quickly see leads with no owner.
      const term = String(search || '').trim().toLowerCase();
      const includeUnassigned = !term || 'unassigned'.includes(term);
      return includeUnassigned ? [UNASSIGNED_OPTION, ...staff] : staff;
    } catch (err) {
      console.error('Failed to search CRM staff:', err);
      return [];
    }
  };

  useEffect(() => {
    if (isCrmStaff && (!availableViewsLoaded || !selectedCrmView)) {
      if (availableViewsLoaded) {
        setLeads([]);
        setLoading(false);
      }
      return undefined;
    }
    const timeout = setTimeout(() => loadLeads(), 250);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, sort, archiveView, selectedCrmView, availableViewsLoaded]);

  const loadSavedFilters = async () => {
    try {
      const res = await getCrmSavedFilters();
      setSavedFilters(res?.data?.filters || []);
    } catch (err) {
      console.error('Failed to load saved filters:', err);
    }
  };

  const buildLeadApiFilters = (overrides = {}) => {
    const apiFilters = {
      page: overrides.page != null ? overrides.page : page,
      limit: overrides.limit != null ? overrides.limit : pagination.limit,
      archived: archiveView === 'archived',
    };
    // A column-header sort (user click) takes precedence over the admin
    // view's configured sort.
    const viewSort = selectedCrmView?.view?.sort;
    if (sort?.field) {
      apiFilters.sortBy = sort.field;
      apiFilters.sortOrder = sort.direction || 'asc';
    } else if (viewSort?.field) {
      apiFilters.sortBy = viewSort.field;
      apiFilters.sortOrder = viewSort.direction || 'asc';
    }
    if (filters.search) apiFilters.search = filters.search;
    if (filters.stage && filters.stage.length) apiFilters.stage = filters.stage;
    if (filters.created_from) apiFilters.created_from = filters.created_from;
    if (filters.tags && filters.tags.length) apiFilters.tags = filters.tags;
    if (filters.cart_active_no_order) apiFilters.cart_active_no_order = true;
    if (filters.unread_whatsapp) apiFilters.unread_whatsapp = true;
    [
      'assigned_user_id',
      'order_presence', 'follow_up_status', 'video_call_status',
      'last_contact_after', 'last_contact_before',
      'pmsg_state', 'pmsg_district', 'pmsg_address',
      'installs_min', 'installs_max',
      'district_installs_min', 'district_installs_max',
      'first_seen_after', 'first_seen_before',
      'last_seen_after', 'last_seen_before',
      'hub_id',
      'oms_matched',
      'registered_after', 'registered_before',
      'last_login_after', 'last_login_before',
      'last_order_after', 'last_order_before',
      'days_since_last_order',
      'orders_30_count_min', 'orders_30_count_max',
      'orders_30_value_min', 'orders_30_value_max',
      'orders_90_count_min', 'orders_90_count_max',
      'orders_90_value_min', 'orders_90_value_max',
      'lifetime_value_min', 'lifetime_value_max',
      'average_order_value_min', 'average_order_value_max',
      'top_brand', 'current_orders_min', 'current_orders_max',
    ].forEach((key) => {
      if (filters[key] !== '' && filters[key] != null) apiFilters[key] = filters[key];
    });
    Object.entries(filters).forEach(([key, value]) => {
      if (key.startsWith('variable_') && value !== '' && value != null) apiFilters[key] = value;
    });
    return apiFilters;
  };

  const loadLeads = async (overrides = {}) => {
    setLoading(true);
    try {
      const apiFilters = buildLeadApiFilters(overrides);
      const res = await getCrmLeads(apiFilters);
      const data = res?.data || {};
      setLeads(data.leads || []);
      if (data.pagination) setPagination(data.pagination);
    } catch (err) {
      console.error('Failed to load leads:', err);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportLeads = async () => {
    if (exportingLeads) return;
    setExportingLeads(true);
    try {
      const activeFilters = buildLeadApiFilters();
      delete activeFilters.page;
      delete activeFilters.limit;
      await downloadCrmLeads(activeFilters);
    } catch (err) {
      alert(err.message || 'Could not export Leads.');
    } finally {
      setExportingLeads(false);
    }
  };

  // Server-side rows-per-page change. Update the limit and reset to page 1,
  // then reload with the new page size.
  const handleChangeRowsPerPage = (newLimit) => {
    setPagination((prev) => ({ ...prev, limit: newLimit }));
    setPage(1);
    loadLeads({ page: 1, limit: newLimit });
  };

  // Server-side sort from a column header click. `column.sortField` carries the
  // backend sort key (whitelisted server-side). Clearing the sort falls back to
  // the default order.
  const handleSort = (column, direction) => {
    setPage(1);
    if (column && column.sortField) {
      setSort({ field: column.sortField, direction });
    } else {
      setSort(null);
    }
  };

  const handleMultiSelectChange = (selected, name) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      [name]: selected ? selected.map((s) => s.value) : [],
    }));
    setSelectedSavedFilter(null);
    if (!isCrmStaff) setSelectedCrmView(null);
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPage(1);
    setFilters((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setSelectedSavedFilter(null);
    if (!isCrmStaff) setSelectedCrmView(null);
  };

  const handleClearFilters = () => {
    if (isCrmStaff && selectedCrmView) {
      setSelectedSavedFilter(null);
      applyFilterSet(selectedCrmView.view.filters);
      return;
    }
    setPage(1);
    setFilters(DEFAULT_FILTERS);
    setSelectedSavedFilter(null);
    setSelectedCrmView(null);
    setAssignedUserOption(null);
  };

  const applyFilterSet = async (nextFilters = {}) => {
    setPage(1);
    setFilters({
      ...DEFAULT_FILTERS,
      ...nextFilters,
      ...(loggedInAssigneeOption
        ? { assigned_user_id: loggedInAssigneeOption.value }
        : {}),
    });
    if (loggedInAssigneeOption) {
      setAssignedUserOption(loggedInAssigneeOption);
      return;
    }
    const savedUserId = nextFilters.assigned_user_id;
    if (!savedUserId) {
      setAssignedUserOption(null);
      return;
    }
    if (savedUserId === 'unassigned') {
      setAssignedUserOption(UNASSIGNED_OPTION);
      return;
    }
    try {
      const res = await searchCrmAssignableStaff('');
      const result = res?.data || res;
      const user = (result?.users || []).find(
        (u) => String(u.id) === String(savedUserId)
      );
      setAssignedUserOption(user ? {
        value: user.id,
        label: user.name || user.email || 'Unnamed user',
      } : {
        value: savedUserId,
        label: 'Previously selected user',
      });
    } catch (err) {
      setAssignedUserOption({ value: savedUserId, label: 'Previously selected user' });
    }
  };

  const handleApplyListView = async (option) => {
    if (!option) {
      handleClearFilters();
      return;
    }
    if (option.kind === 'crm-view') {
      setSelectedCrmView(option);
      setSelectedSavedFilter(null);
      await applyFilterSet(option.view.filters);
      return;
    }
    setSelectedSavedFilter(option);
    setSelectedCrmView(null);
    const saved = savedFilters.find((item) => item.id === option.sourceId);
    if (saved) await applyFilterSet(saved.filters);
  };

  const handleSaveFilter = async () => {
    const name = window.prompt('Name this filter:');
    if (!name) return;
    try {
      await createCrmSavedFilter(name, filters);
      await loadSavedFilters();
      alert('Filter saved.');
    } catch (err) {
      alert(err.message || 'Failed to save filter');
    }
  };

  const handleDeleteSavedFilter = async () => {
    if (!selectedSavedFilter) return;
    if (!window.confirm(`Delete saved filter "${selectedSavedFilter.label}"?`)) return;
    try {
      await deleteCrmSavedFilter(selectedSavedFilter.sourceId);
      setSelectedSavedFilter(null);
      await loadSavedFilters();
    } catch (err) {
      alert(err.message || 'Failed to delete filter');
    }
  };

  const canRunBulkActions = ['manager', 'admin', 'superAdmin'].includes(userData.role);
  const canAdministerLeads = ['manager', 'admin', 'superAdmin'].includes(userData.role);
  const canImportLeads = ['manager', 'superAdmin'].includes(userData.role);
  const canUseCrmCalling = ['manager', 'superAdmin'].includes(userData.role) ||
    userData.access?.includes('crm');

  const clearSelection = () => {
    setSelectedRows([]);
    setClearSelectedRowsToggle((current) => !current);
  };

  // Remaining bulk actions are phase-one placeholders.
  const bulkStub = (label) =>
    alert(`${label} — available when bulk actions land (spec §12). ${selectedRows.length} lead(s) selected.`);

  const getStageBadge = (stage) => {
    const cls =
      stage === 'active'
        ? 'status-badge status-confirmed'
        : stage === 'inactive'
        ? 'status-badge status-pending'
        : 'status-badge';
    return <span className={cls}>{STAGE_LABELS[stage] || stage}</span>;
  };

  const crmViewOptions = availableViews.map((view) => ({
    value: `crm-view-${view.id}`,
    sourceId: view.id,
    label: view.name,
    kind: 'crm-view',
    view,
  }));
  const savedFilterOptions = savedFilters.map((filter) => ({
    value: `saved-filter-${filter.id}`,
    sourceId: filter.id,
    label: filter.name,
    kind: 'saved-filter',
  }));
  const listViewOptions = isCrmStaff
    ? (crmViewOptions.length ? [{ label: 'Assigned views', options: crmViewOptions }] : [])
    : [
        ...(crmViewOptions.length ? [{ label: 'CRM views', options: crmViewOptions }] : []),
        ...(savedFilterOptions.length ? [{ label: 'My saved filters', options: savedFilterOptions }] : []),
      ];
  const stageValue = STAGE_OPTIONS.filter((o) => (filters.stage || []).includes(o.value));

  const tagValue = (filters.tags || []).map((t) => ({ value: t, label: t }));

  const columns = useMemo(
    () => [
      {
        name: 'Lead',
        key: 'lead',
        id: 'displayName',
        selector: (row) => row.displayName || '',
        cell: (row) => (
          <div className="crm-lead-cell">
            <div className="crm-lead-title-row">
              <Link
                to={`/crm/leads/${row.id}`}
                className="crm-lead-name"
                target={isMobile ? undefined : '_blank'}
                rel="noopener noreferrer"
              >
                {row.displayName || 'Unnamed lead'}
              </Link>
              {row.unreadWhatsapp > 0 && (
                <span className="crm-wa-badge">WA {row.unreadWhatsapp}</span>
              )}
              {row.videoCall?.status && (
                <span
                  className={`crm-vc-badge crm-vc-badge-${row.videoCall.status}`}
                  title={row.videoCall.status === 'requested'
                    ? 'Pending video call request'
                    : 'Video call completed'}
                  aria-label={row.videoCall.status === 'requested'
                    ? 'Pending video call request'
                    : 'Video call completed'}
                >
                  VC
                </span>
              )}
            </div>
            {row.primaryContactName && (
              <div className="crm-muted">{row.primaryContactName}</div>
            )}
          </div>
        ),
        minWidth: '210px',
      },
      {
        name: 'Stage',
        key: 'stage',
        id: 'stage',
        selector: (row) => row.stage,
        cell: (row) => {
          const editable = !row.archived && !['active', 'inactive'].includes(row.stage);
          if (!editable) return getStageBadge(row.stage);
          return (
            <button
              type="button"
              className="crm-list-stage-trigger"
              onClick={() => setStageTarget(row)}
              title="Click to edit stage"
              aria-label={`Edit stage for ${row.displayName || `Lead ${row.id}`}`}
            >
              {getStageBadge(row.stage)}
              <span aria-hidden="true">✎</span>
            </button>
          );
        },
        minWidth: '155px',
        maxWidth: '180px',
      },
      {
        name: 'Tags',
        key: 'tags',
        selector: (row) => (row.tags || []).join(', '),
        cell: (row) => compactChips(row.tags || [], (tag) => tag),
        minWidth: '145px',
      },
      {
        name: 'Hub',
        key: 'hub',
        sortable: true,
        sortField: 'hub',
        selector: (row) =>
          (row.hubLinks || []).map((hub) => hub.name).filter(Boolean).join(', ') ||
          row.suggestedHub?.name ||
          '—',
        cell: (row) => {
          if ((row.hubLinks || []).length) {
            return compactChips(
              row.hubLinks,
              (hub) => {
                const details = [
                  hub.serviceable === true ? 'Serviceable' : null,
                  hub.serviceable === false ? 'Not serviceable' : null,
                  hub.distanceKm != null ? `${hub.distanceKm} km` : null,
                ].filter(Boolean);
                return `${hub.name || `Hub #${hub.hubId}`}${details.length ? ` · ${details.join(' · ')}` : ''}`;
              },
              'No hub'
            );
          }
          if (canRunBulkActions && row.suggestedHub) {
            return (
              <div className="crm-hub-suggestion" title="Review and confirm this suggested Hub">
                <span className="crm-hub-suggestion-label">Suggested</span>
                <span>{row.suggestedHub.name || `Hub #${row.suggestedHub.hubId}`}</span>
              </div>
            );
          }
          return <span className="crm-muted">No hub</span>;
        },
        minWidth: '150px',
      },
      {
        name: 'Owner',
        key: 'owner',
        sortable: true,
        sortField: 'owner',
        selector: (row) => row.assignedUser?.name || row.assignedUser?.email || 'Unassigned',
        minWidth: '130px',
      },
      {
        name: 'District',
        key: 'district',
        selector: (row) => row.district || '—',
        cell: (row) => {
          const districts = row.pmsg?.districts || [];
          if (!districts.length) return <span className="crm-muted">—</span>;
          const primaryDistrict = districts[0];
          return (
            <div>
              <div>{primaryDistrict.district}</div>
              {districts.length > 1 && (
                <div className="crm-muted">+{districts.length - 1} more</div>
              )}
            </div>
          );
        },
        minWidth: '120px',
      },
      {
        name: 'Address',
        key: 'address',
        selector: (row) => row.address || 'N/A',
        cell: (row) => {
          const addresses = row.pmsg?.addresses || (row.address ? [row.address] : []);
          if (!addresses.length) return <span className="crm-muted">N/A</span>;
          return (
            <div className="crm-address-cell" title={addresses.join('\n')}>
              <div className="crm-address-text">{addresses[0]}</div>
              {addresses.length > 1 && (
                <div className="crm-muted">+{addresses.length - 1} more</div>
              )}
            </div>
          );
        },
        minWidth: '240px',
      },
      {
        name: 'Installs',
        key: 'installs',
        sortable: true,
        sortField: 'installs',
        selector: (row) => (row.installs != null ? row.installs : '—'),
        cell: (row) => {
          if (row.installs == null) return <span className="crm-muted">—</span>;
          return (
            <div>
              <div>{Number(row.installs).toLocaleString('en-IN')}</div>
              {row.pmsg?.installsLast4Weeks && (
                <div className="crm-muted">
                  +{Number(row.pmsg.installsLast4Weeks.national).toLocaleString('en-IN')} / 4 wk
                </div>
              )}
            </div>
          );
        },
        width: '110px',
      },
      {
        name: 'Orders (30 / 90d)',
        key: 'orders',
        sortable: true,
        sortField: 'orders_count',
        selector: (row) => (row.orders ? row.orders.count30 : 0),
        cell: (row) => {
          const o = row.orders || {};
          return (
            <div>
              <div>{`${o.count30 || 0} / ${o.count90 || 0}`}</div>
              {o.value90 != null && <div className="crm-muted">{money(o.value90)} (90d)</div>}
            </div>
          );
        },
        minWidth: '130px',
      },
      {
        name: 'Last order',
        key: 'last_order',
        sortable: true,
        sortField: 'last_order_date',
        selector: (row) =>
          row.lastOrderDate ? new Date(row.lastOrderDate).toLocaleDateString() : '—',
      },
      {
        name: 'Last contact',
        key: 'last_contact',
        sortable: true,
        sortField: 'last_contact_date',
        selector: (row) =>
          row.lastContactDate ? new Date(row.lastContactDate).toLocaleDateString() : '—',
      },
      {
        name: 'App activity',
        key: 'app_activity',
        selector: (row) => row.appAndCart?.accounts?.[0]?.lastLoginAt || '',
        cell: (row) => accountActivity(row),
        minWidth: '245px',
      },
      {
        name: 'Follow-up',
        key: 'follow_up',
        sortable: true,
        sortField: 'follow_up_date',
        selector: (row) => row.followUpDate || '',
        cell: (row) => {
          if (!row.followUpDate) return <span className="crm-followup-empty">Not scheduled</span>;
          const overdue = isOverdue(row.followUpDate);
          return (
            <span className={overdue ? 'crm-followup crm-followup-overdue' : 'crm-followup'}>
              {new Date(row.followUpDate).toLocaleDateString()}
              {overdue ? ' · Overdue' : ''}
            </span>
          );
        },
        minWidth: '175px',
      },
      ...variableDefinitions.map((variable) => ({
        name: variable.label,
        key: `variable_${variable.key}`,
        selector: (row) => row.variables?.[variable.key] ?? '',
        cell: (row) => row.variables?.[variable.key] || <span className="crm-muted">—</span>,
        minWidth: '140px',
      })),
      {
        key: 'actions',
        cell: (row) => (
          <div className={`crm-row-actions${canAdministerLeads ? ' crm-row-actions-admin' : ''}`}>
            {canUseCrmCalling && (row.callable || (
              isMobile && deviceDialNumber(row.primaryContactDirectDialPhone)
            )) && (
              <button
                className="btn btn-success crm-btn-xs"
                title={row.callable
                  ? 'Call the primary contact via CRM'
                  : 'Call the primary contact using your phone dialer'}
                onClick={() => {
                  if (row.callable) {
                    setCallTarget(row);
                    return;
                  }
                  setDialTarget({
                    name: row.primaryContactName,
                    phone: row.primaryContactDirectDialPhone,
                  });
                }}
              >
                Call
              </button>
            )}
            <Link
              to={`/crm/leads/${row.id}`}
              className="btn btn-secondary crm-btn-xs"
              target={isMobile ? undefined : '_blank'}
              rel="noopener noreferrer"
            >
              View
            </Link>
            {canAdministerLeads && (
              <button
                className="btn btn-secondary crm-btn-xs"
                onClick={() => setArchiveTarget(row)}
              >
                {row.archived ? 'Unarchive' : 'Archive'}
              </button>
            )}
            {canAdministerLeads && (
              <button className="btn btn-danger crm-btn-xs" onClick={() => setDeleteTarget(row)}>
                Delete
              </button>
            )}
          </div>
        ),
        minWidth: canAdministerLeads ? '190px' : '135px',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canAdministerLeads, canUseCrmCalling, variableDefinitions, isMobile]
  );

  const tableColumns = useMemo(() => {
    if (!selectedCrmView) return columns;
    const configured = new Set(selectedCrmView.view.columns || []);
    return columns.filter((column) => configured.has(column.key));
  }, [columns, selectedCrmView]);

  const savedViewName = selectedCrmView
    ? selectedCrmView.label
    : selectedSavedFilter
    ? selectedSavedFilter.label
    : archiveView === 'archived' ? 'Archived leads' : 'Active leads';
  const activeOrderFilterCount = ORDER_FILTER_GROUPS.filter(
    (keys) => keys.some((key) => filters[key] !== '' && filters[key] != null)
  ).length;
  const activePmsgInstallFilterCount = PMSG_INSTALL_FILTER_GROUPS.filter(
    (keys) => keys.some((key) => filters[key] !== '' && filters[key] != null)
  ).length;
  const activeAdvancedFilterCount = ADVANCED_FILTER_KEYS.filter(
    (key) => filters[key] !== '' && filters[key] != null
  ).length + activePmsgInstallFilterCount + activeOrderFilterCount + variableDefinitions.filter((variable) => {
    const value = filters[`variable_${variable.key}`];
    return value !== '' && value != null;
  }).length;

  return (
    <div className="container">
      {/* Header + count summary */}
      <div className="crm-list-header">
        <div className="crm-list-title-block">
          <div className="crm-list-title-icon" aria-hidden="true">L</div>
          <div>
            <h2 className="page-title">Leads</h2>
            <div className="crm-list-count">
              <strong>{pagination.total.toLocaleString('en-IN')}</strong> lead{pagination.total === 1 ? '' : 's'} · {savedViewName}
            </div>
          </div>
        </div>
        <div className="crm-list-header-actions">
          {canAdministerLeads && (
            <div className="crm-view-toggle" role="group" aria-label="Lead archive view">
              <button
                type="button"
                className={archiveView === 'active' ? 'active' : ''}
                onClick={() => { setArchiveView('active'); setPage(1); clearSelection(); }}
              >Active</button>
              <button
                type="button"
                className={archiveView === 'archived' ? 'active' : ''}
                onClick={() => { setArchiveView('archived'); setPage(1); clearSelection(); }}
              >Archived</button>
            </div>
          )}
          <div className="crm-list-utility-actions">
            {canRunBulkActions && (
              <button type="button" className="btn btn-secondary" style={isMobile ? { minHeight: '40px' } : undefined} onClick={() => setVariableCsvDialogOpen(true)}>Import variables</button>
            )}
            {canImportLeads && (
              <button type="button" className="btn btn-secondary" style={isMobile ? { minHeight: '40px' } : undefined} onClick={() => setLeadCsvDialogOpen(true)}>Import leads</button>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              style={isMobile ? { minHeight: '40px' } : undefined}
              onClick={handleExportLeads}
              disabled={exportingLeads}
            >
              {exportingLeads ? 'Exporting…' : 'Export'}
            </button>
          </div>
          <Link to="/crm/leads/create" className="btn btn-primary crm-list-new-lead">+ New lead</Link>
        </div>
      </div>

      {/* Horizontal filter bar */}
      <div className={`filters-card crm-lead-filters-card${isMobile && !filtersOpen ? ' mobile-collapsed' : ''}`}>
        <div className="crm-filter-card-header">
          <div>
            <div className="crm-filter-card-title">Filter leads</div>
            <div className="crm-filter-card-subtitle">Find leads by CRM, PMSG, app and order activity.</div>
          </div>
          <div className="crm-filter-header-actions">
            {isMobile && (
              <button type="button" className="btn btn-secondary mobile-filters-toggle" onClick={() => setFiltersOpen((open) => !open)}>
                {filtersOpen ? 'Hide filters' : 'Filters'}
              </button>
            )}
            <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={handleSaveFilter}>Save filter</button>
            {selectedSavedFilter && (
              <button className="btn btn-danger" onClick={handleDeleteSavedFilter}>Delete saved</button>
            )}
          </div>
        </div>

        <div className="crm-filter-bar crm-primary-filter-bar">
          <div className="crm-filter-item crm-filter-saved">
            <Select
              options={listViewOptions}
              styles={selectStyles}
              isSearchable={!isMobile || (crmViewOptions.length + savedFilterOptions.length) > 8}
              value={selectedCrmView || selectedSavedFilter}
              onChange={handleApplyListView}
              placeholder={isCrmStaff ? 'No assigned views' : 'All leads'}
              isClearable={!isCrmStaff}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          <div className="crm-filter-grow crm-filter-search">
            <input
              type="text"
              name="search"
              className="form-control"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search name, contact, phone, vendor id"
            />
          </div>

          <div className="crm-filter-item crm-filter-stage">
            <Select
              options={STAGE_OPTIONS}
              isMulti
              isSearchable={!isMobile || STAGE_OPTIONS.length > 8}
              styles={selectStyles}
              value={stageValue}
              onChange={(selected) => handleMultiSelectChange(selected, 'stage')}
              placeholder="Stage: all"
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          <div className="crm-filter-item crm-filter-source">
            <select
              name="created_from"
              className="form-control"
              value={filters.created_from}
              onChange={handleFilterChange}
            >
              <option value="">Source: all</option>
              {CREATED_FROM_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="crm-filter-item crm-filter-tags">
            <Select
              options={tagOptions}
              isMulti
              isSearchable={!isMobile || tagOptions.length > 8}
              styles={selectStyles}
              value={tagValue}
              onChange={(selected) => handleMultiSelectChange(selected, 'tags')}
              placeholder="Tags: all"
              isClearable
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          <div className="crm-filter-quick-toggles">
            <select
              name="order_presence"
              className="form-control crm-filter-compact-select"
              value={filters.order_presence}
              onChange={handleFilterChange}
            >
              <option value="">Orders: all</option>
              <option value="has_orders">Has orders</option>
              <option value="zero_orders">Zero orders</option>
            </select>
            <select
              name="follow_up_status"
              className="form-control crm-filter-compact-select"
              value={filters.follow_up_status}
              onChange={handleFilterChange}
            >
              <option value="">Follow-up: all</option>
              <option value="overdue">{isCrmStaff ? 'My overdue' : 'Overdue'}</option>
            </select>
            <select
              name="video_call_status"
              className="form-control crm-filter-compact-select"
              value={filters.video_call_status}
              onChange={handleFilterChange}
            >
              <option value="">Video call: all</option>
              <option value="requested">Requested</option>
              <option value="completed">Completed</option>
            </select>
            <label className="crm-check-inline">
              <input type="checkbox" name="unread_whatsapp"
                checked={filters.unread_whatsapp} onChange={handleFilterChange} />
              Unread WhatsApp
            </label>
            <label className="crm-check-inline">
              <input type="checkbox" name="cart_active_no_order"
                checked={filters.cart_active_no_order} onChange={handleFilterChange} />
              Active cart, no recent order
            </label>
          </div>
        </div>

        <details className="crm-advanced-filter-panel">
          <summary>
            <span className="crm-advanced-filter-summary-text">
              <strong>More filters</strong>
              <small>Assignment, location, PMSG, dates and order value</small>
            </span>
            {activeAdvancedFilterCount > 0 && (
              <span className="crm-filter-count">{activeAdvancedFilterCount} active</span>
            )}
          </summary>
          <div className="crm-advanced-filter-body">
        {/* Spec §14 filters (second row) */}
        <div className="crm-advanced-filter-grid">
          <div className="crm-filter-item">
            <AsyncSelect
              styles={selectStyles}
              cacheOptions
              defaultOptions
              isClearable={!isCrmStaff}
              isDisabled={isCrmStaff}
              loadOptions={loadStaffOptions}
              value={assignedUserOption}
              onChange={(option) => {
                if (isCrmStaff) return;
                setPage(1);
                setAssignedUserOption(option || null);
                setFilters((prev) => ({ ...prev, assigned_user_id: option ? option.value : '' }));
                setSelectedSavedFilter(null);
                if (!isCrmStaff) setSelectedCrmView(null);
              }}
              placeholder="Assigned user"
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div className="crm-filter-item">
            <input type="text" name="pmsg_state" className="form-control"
              value={filters.pmsg_state} onChange={handleFilterChange}
              placeholder="PMSG state" />
          </div>
          <div className="crm-filter-item">
            <input type="text" name="pmsg_district" className="form-control"
              value={filters.pmsg_district} onChange={handleFilterChange}
              placeholder="PMSG district" />
          </div>
          <div className="crm-filter-item">
            <input type="text" name="pmsg_address" className="form-control"
              value={filters.pmsg_address} onChange={handleFilterChange}
              placeholder="PMSG address" />
          </div>
          <div className="crm-filter-item">
            <Select
              styles={selectStyles}
              options={hubOptions}
              isSearchable={!isMobile || hubOptions.length > 8}
              isClearable
              value={hubOptions.find((o) => String(o.value) === String(filters.hub_id)) || null}
              onChange={(option) => {
                setPage(1);
                setFilters((prev) => ({ ...prev, hub_id: option ? option.value : '' }));
                setSelectedSavedFilter(null);
                if (!isCrmStaff) setSelectedCrmView(null);
              }}
              placeholder="Hub"
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          <div className="crm-filter-item">
            <select name="oms_matched" className="form-control"
              value={filters.oms_matched} onChange={handleFilterChange}>
              <option value="">App user: all</option>
              <option value="true">App user: yes</option>
              <option value="false">App user: no</option>
            </select>
          </div>
          <div className="crm-filter-item">
            <input type="number" name="days_since_last_order" className="form-control"
              value={filters.days_since_last_order} onChange={handleFilterChange}
              placeholder="Days since last order ≥" min="1" />
          </div>
        </div>

        <div className="crm-pmsg-install-panel">
          <div className="crm-pmsg-install-heading">
            <strong>PMSG install ranges</strong>
            <span>Filter national totals or installs in the matching district.</span>
          </div>
          <div className="crm-pmsg-install-grid">
            <RangeFilter label="National installs"
              minName="installs_min" maxName="installs_max"
              filters={filters} onChange={handleFilterChange} />
            <RangeFilter label="District installs"
              minName="district_installs_min" maxName="district_installs_max"
              filters={filters} onChange={handleFilterChange} />
          </div>
        </div>

        {/* Spec §14 date-range filters (third row) */}
        <div className="crm-date-filter-grid">
          <div className="crm-filter-item">
            <label className="crm-muted" style={{ display: 'block', fontSize: '11px' }}>Last contact</label>
            <div className="crm-date-range-inputs">
              <input type="date" name="last_contact_after" className="form-control"
                value={filters.last_contact_after} onChange={handleFilterChange} />
              <input type="date" name="last_contact_before" className="form-control"
                value={filters.last_contact_before} onChange={handleFilterChange} />
            </div>
          </div>
          <div className="crm-filter-item">
            <label className="crm-muted" style={{ display: 'block', fontSize: '11px' }}>Last order</label>
            <div className="crm-date-range-inputs">
              <input type="date" name="last_order_after" className="form-control"
                value={filters.last_order_after} onChange={handleFilterChange} />
              <input type="date" name="last_order_before" className="form-control"
                value={filters.last_order_before} onChange={handleFilterChange} />
            </div>
          </div>
          <div className="crm-filter-item">
            <label className="crm-muted" style={{ display: 'block', fontSize: '11px' }}>First seen (PMSG)</label>
            <div className="crm-date-range-inputs">
              <input type="date" name="first_seen_after" className="form-control"
                value={filters.first_seen_after} onChange={handleFilterChange} />
              <input type="date" name="first_seen_before" className="form-control"
                value={filters.first_seen_before} onChange={handleFilterChange} />
            </div>
          </div>
          <div className="crm-filter-item">
            <label className="crm-muted" style={{ display: 'block', fontSize: '11px' }}>Last seen (PMSG)</label>
            <div className="crm-date-range-inputs">
              <input type="date" name="last_seen_after" className="form-control"
                value={filters.last_seen_after} onChange={handleFilterChange} />
              <input type="date" name="last_seen_before" className="form-control"
                value={filters.last_seen_before} onChange={handleFilterChange} />
            </div>
          </div>
          <div className="crm-filter-item">
            <label className="crm-muted" style={{ display: 'block', fontSize: '11px' }}>Registered</label>
            <div className="crm-date-range-inputs">
              <input type="date" name="registered_after" className="form-control"
                value={filters.registered_after} onChange={handleFilterChange} />
              <input type="date" name="registered_before" className="form-control"
                value={filters.registered_before} onChange={handleFilterChange} />
            </div>
          </div>
          <div className="crm-filter-item">
            <label className="crm-muted" style={{ display: 'block', fontSize: '11px' }}>Last login</label>
            <div className="crm-date-range-inputs">
              <input type="date" name="last_login_after" className="form-control"
                value={filters.last_login_after} onChange={handleFilterChange} />
              <input type="date" name="last_login_before" className="form-control"
                value={filters.last_login_before} onChange={handleFilterChange} />
            </div>
          </div>
        </div>

        <details className="crm-order-filter-panel">
          <summary>
            <span>Order value and activity</span>
            {activeOrderFilterCount > 0 && (
              <span className="crm-filter-count">{activeOrderFilterCount} active</span>
            )}
          </summary>
          <div className="crm-order-filter-grid">
            <RangeFilter label="30-day order count"
              minName="orders_30_count_min" maxName="orders_30_count_max"
              filters={filters} onChange={handleFilterChange} />
            <RangeFilter label="30-day order value"
              minName="orders_30_value_min" maxName="orders_30_value_max"
              filters={filters} onChange={handleFilterChange} moneyRange />
            <RangeFilter label="90-day order count"
              minName="orders_90_count_min" maxName="orders_90_count_max"
              filters={filters} onChange={handleFilterChange} />
            <RangeFilter label="90-day order value"
              minName="orders_90_value_min" maxName="orders_90_value_max"
              filters={filters} onChange={handleFilterChange} moneyRange />
            <RangeFilter label="Lifetime value"
              minName="lifetime_value_min" maxName="lifetime_value_max"
              filters={filters} onChange={handleFilterChange} moneyRange />
            <RangeFilter label="Average order value"
              minName="average_order_value_min" maxName="average_order_value_max"
              filters={filters} onChange={handleFilterChange} moneyRange />
            <RangeFilter label="Current orders"
              minName="current_orders_min" maxName="current_orders_max"
              filters={filters} onChange={handleFilterChange} />
            <div className="crm-range-filter">
              <label htmlFor="crm-top-brand-filter">Top brand</label>
              <input id="crm-top-brand-filter" type="text" name="top_brand"
                className="form-control" value={filters.top_brand}
                onChange={handleFilterChange} placeholder="Brand name" />
            </div>
          </div>
        </details>
        {variableDefinitions.length > 0 && (
          <div className="crm-order-filter-panel" style={{ marginTop: '10px' }}>
            <div className="crm-order-filter-grid">
              {variableDefinitions.map((variable) => {
                const name = `variable_${variable.key}`;
                return (
                  <div className="crm-range-filter" key={variable.id}>
                    <label htmlFor={`crm-${name}`}>{variable.label}</label>
                    {variable.type === 'list' ? (
                      <select id={`crm-${name}`} name={name} className="form-control" value={filters[name] || ''} onChange={handleFilterChange}>
                        <option value="">Any value</option>
                        {(variable.allowedValues || []).map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    ) : (
                      <input
                        id={`crm-${name}`}
                        name={name}
                        className="form-control"
                        type={variable.type === 'date' ? 'date' : variable.type === 'number' ? 'number' : 'text'}
                        step={variable.type === 'number' ? 'any' : undefined}
                        value={filters[name] || ''}
                        onChange={handleFilterChange}
                        placeholder={`Filter ${variable.label}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
          </div>
        </details>
      </div>

      {/* Bulk action bar (managers/admins) */}
      {archiveView === 'active' && selectedRows.length > 0 && (
        <div className="crm-bulk-bar">
          <span className="crm-bulk-count">{selectedRows.length} selected</span>
          {canRunBulkActions && (
            <button className="btn btn-secondary" onClick={() => setBulkHubDialogOpen(true)}>Assign hub</button>
          )}
          {canRunBulkActions && selectedRows.some((lead) => lead.suggestedHub && !(lead.hubLinks || []).length) && (
            <button className="btn btn-primary" onClick={() => setConfirmHubSuggestionsOpen(true)}>
              Confirm suggested hubs
            </button>
          )}
          {canRunBulkActions && (
            <button className="btn btn-secondary" onClick={() => setBulkUserDialogOpen(true)}>Assign user</button>
          )}
          <button className="btn btn-secondary" onClick={() => setBulkTagDialogOpen(true)}>Add/remove tag</button>
          {canRunBulkActions && (
            <button className="btn btn-secondary" onClick={() => setBulkVariableDialogOpen(true)}>Set variable</button>
          )}
          <button className="btn btn-secondary" onClick={() => bulkStub('Send WhatsApp template')}>Send WhatsApp template</button>
          <button className="btn btn-secondary" onClick={clearSelection}>Clear selection</button>
        </div>
      )}

      <div className="card crm-lead-table-card">
        <DataTable
          key={selectedCrmView?.value || 'all-columns'}
          columns={tableColumns}
          mobileSummaryFields={['displayName', 'Lead', 'stage', 'Stage', 'Hub', 'Owner', 'Last contact', 'Orders (30 / 90d)']}
          data={leads}
          progressPending={loading}
          selectableRows={archiveView === 'active'}
          clearSelectedRows={clearSelectedRowsToggle}
          onSelectedRowsChange={({ selectedRows: rows }) => setSelectedRows(rows)}
          pagination
          paginationServer
          paginationTotalRows={pagination.total}
          paginationDefaultPage={page}
          paginationPerPage={pagination.limit}
          paginationRowsPerPageOptions={[10, 25, 50, 75, 100]}
          onChangePage={(p) => setPage(p)}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          sortServer
          onSort={handleSort}
          customStyles={customStyles}
          highlightOnHover
          striped
          noDataComponent={<div style={{ padding: '24px' }}>No leads found.</div>}
        />
      </div>

      {callTarget && (
        <CrmCallDialog
          leadId={callTarget.id}
          contact={{ id: callTarget.primaryContactId, name: callTarget.primaryContactName }}
          hubLinks={callTarget.hubLinks || []}
          onClose={() => setCallTarget(null)}
          onLogged={() => {
            setCallTarget(null);
            loadLeads();
          }}
        />
      )}
      {dialTarget && (
        <CrmDeviceDialDialog contact={dialTarget} onClose={() => setDialTarget(null)} />
      )}
      {archiveTarget && (
        <CrmArchiveLeadDialog
          lead={archiveTarget}
          restore={Boolean(archiveTarget.archived)}
          onClose={() => setArchiveTarget(null)}
          onSaved={async () => {
            setArchiveTarget(null);
            clearSelection();
            await loadLeads();
          }}
        />
      )}
      {deleteTarget && (
        <CrmDeleteLeadDialog
          lead={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={async () => {
            setDeleteTarget(null);
            clearSelection();
            await loadLeads();
          }}
        />
      )}
      {stageTarget && (
        <CrmStageDialog
          lead={stageTarget}
          onClose={() => setStageTarget(null)}
          onSaved={async () => {
            setStageTarget(null);
            await loadLeads();
          }}
        />
      )}
      {bulkHubDialogOpen && (
        <CrmBulkAssignHubDialog
          leadIds={selectedRows.map((lead) => lead.id)}
          onClose={() => setBulkHubDialogOpen(false)}
          onSaved={async () => {
            clearSelection();
            await loadLeads();
          }}
        />
      )}
      {confirmHubSuggestionsOpen && (
        <CrmBulkConfirmHubSuggestionsDialog
          leads={selectedRows}
          onClose={() => setConfirmHubSuggestionsOpen(false)}
          onSaved={async () => {
            clearSelection();
            await loadLeads();
          }}
        />
      )}
      {bulkUserDialogOpen && (
        <CrmBulkAssignUserDialog
          leadIds={selectedRows.map((lead) => lead.id)}
          onClose={() => setBulkUserDialogOpen(false)}
          onSaved={async () => {
            clearSelection();
            await loadLeads();
          }}
        />
      )}
      {bulkTagDialogOpen && (
        <CrmBulkTagDialog
          leadIds={selectedRows.map((lead) => lead.id)}
          tagOptions={tagOptions}
          onClose={() => setBulkTagDialogOpen(false)}
          onSaved={async () => {
            clearSelection();
            await Promise.all([loadLeads(), loadTagOptions()]);
          }}
        />
      )}
      {bulkVariableDialogOpen && (
        <CrmBulkSetVariableDialog
          leadIds={selectedRows.map((lead) => lead.id)}
          onClose={() => setBulkVariableDialogOpen(false)}
          onSaved={async () => {
            clearSelection();
            await loadLeads();
          }}
        />
      )}
      {variableCsvDialogOpen && (
        <CrmVariableCsvImportDialog
          onClose={() => setVariableCsvDialogOpen(false)}
          onImported={loadLeads}
        />
      )}
      {leadCsvDialogOpen && (
        <CrmLeadCsvImportDialog
          hubOptions={hubOptions}
          onClose={() => setLeadCsvDialogOpen(false)}
          onImported={loadLeads}
        />
      )}
    </div>
  );
};

export default CrmLeads;
